import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, CalendarCheck, MessageCircle, Phone } from "lucide-react";
import site, { waLink } from "@/config/site";
import { LeadForm } from "@/components/landing/Connect";
import RazorpayButton from "@/components/RazorpayButton";

export default function Book() {
  const payment = site.payments.growthCall;

  return (
    <div data-testid="book-page" className="min-h-screen bg-[#0A0F08] text-[#F2F1E8]">
      <div className="border-b border-[#27331C]">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="book-home-link">
            <img src="/logo-sanvalio.png" alt="Sanvalio" className="h-8 w-auto" />
          </Link>
          <span className="font-mono2 text-[10px] tracking-[0.3em] text-[#8A8C7E]">₹999 GROWTH CALL</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-16 sm:py-24 grid lg:grid-cols-2 gap-14 items-start">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="font-mono2 text-[10px] tracking-[0.3em] text-[var(--accent)]">YOU'RE ONE STEP AWAY</span>
          <h1 className="mt-3 font-display text-4xl sm:text-6xl leading-[1.02]">
            Lock your <span className="italic font-light text-[var(--accent)]">Growth Call.</span>
          </h1>
          <p className="mt-5 text-sm sm:text-base text-[#B9BBAC] leading-relaxed max-w-md">
            60 minutes. Your brand, dissected. A roadmap you can execute the same week —
            and the ₹999 is fully adjusted when we execute together.
          </p>

          <div className="mt-9 space-y-4">
            <div className="flex items-start gap-4">
              <span className="font-mono2 text-xs text-[var(--accent)] pt-1">01</span>
              <div>
                <p className="text-sm font-semibold text-[#F2F1E8]">Share your details</p>
                <p className="text-xs text-[#8A8C7E] mt-1">So your roadmap, payment link and slot confirmation reach the right inbox.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="font-mono2 text-xs text-[var(--accent)] pt-1">02</span>
              <div>
                <p className="text-sm font-semibold text-[#F2F1E8]">Pay ₹{payment.amount} securely via Razorpay</p>
                <p className="text-xs text-[#8A8C7E] mt-1">UPI, cards, netbanking — instant confirmation on email.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="font-mono2 text-xs text-[var(--accent)] pt-1">03</span>
              <div>
                <p className="text-sm font-semibold text-[#F2F1E8]">Pick your slot</p>
                <p className="text-xs text-[#8A8C7E] mt-1">Choose a time that suits you — calendar link arrives right after payment.</p>
              </div>
            </div>
          </div>

          {payment.buttonId ? (
            <RazorpayButton buttonId={payment.buttonId} />
          ) : (
            payment.link && (
              <a href={payment.link} target="_blank" rel="noopener noreferrer" data-testid="book-pay-btn"
                className="mt-9 inline-flex items-center gap-3 bg-[var(--accent)] text-[#0A0F08] font-bold text-sm sm:text-base px-8 py-4 rounded-full hover:bg-[#F2F1E8] transition-colors duration-300">
                <CreditCard className="w-5 h-5" /> Pay ₹{payment.amount} via Razorpay
              </a>
            )
          )}

          {site.schedule.calendly && (
            <a href={site.schedule.calendly} target="_blank" rel="noopener noreferrer" data-testid="book-schedule-btn"
              className="mt-8 inline-flex items-center gap-3 border border-[var(--accent)] text-[var(--accent)] font-bold text-sm sm:text-base px-8 py-4 rounded-full hover:bg-[var(--accent)] hover:text-[#0A0F08] transition-colors duration-300">
              <CalendarCheck className="w-5 h-5" /> Pick Your Slot — Free Consultation
            </a>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {site.contact.whatsapp && (
              <a href={waLink("Hi! I want to book the ₹999 Growth Call.")}
                target="_blank" rel="noopener noreferrer" data-testid="book-whatsapp-btn"
                className="flex items-center gap-2 border border-[#27331C] text-[#F2F1E8] text-sm px-6 py-3 rounded-full hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors duration-300">
                <MessageCircle className="w-4 h-4" /> WhatsApp instead
              </a>
            )}
            {site.contact.phone && (
              <a href={`tel:${site.contact.phone}`} data-testid="book-call-btn"
                className="flex items-center gap-2 border border-[#27331C] text-[#F2F1E8] text-sm px-6 py-3 rounded-full hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors duration-300">
                <Phone className="w-4 h-4" /> Call directly
              </a>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
          className="rounded-3xl border border-[#27331C] bg-[#0E140A] p-7 sm:p-9 lg:sticky lg:top-24">
          <span className="font-mono2 text-[10px] tracking-[0.3em] text-[var(--accent)]">STEP 01</span>
          <h2 className="mt-2 font-display text-3xl">Your details</h2>
          <p className="mt-2 text-xs text-[#8A8C7E] mb-7">Goes straight to the founder. Nothing is shared, ever.</p>
          <LeadForm plan="growth-call-999" compact />
        </motion.div>
      </div>
    </div>
  );
}
